import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthedApi, errMsg } from "./api";

const FavCtx = createContext({ ids: new Set(), toggle: () => {} });

export function FavoritesProvider({ children }) {
  const { isSignedIn } = useAuth();
  const api = useAuthedApi();
  const navigate = useNavigate();
  const [ids, setIds] = useState(new Set());

  useEffect(() => {
    if (!isSignedIn) { setIds(new Set()); return; }
    api("get", "/favorites").then((r) => setIds(new Set(r.ids))).catch(() => {});
  }, [isSignedIn, api]);

  const pending = useRef(new Set());
  const toggle = useCallback(async (id) => {
    if (!isSignedIn) { navigate(`/sign-in?redirect_url=${encodeURIComponent(window.location.href)}`); return; }
    if (pending.current.has(id)) return;
    pending.current.add(id);
    const has = ids.has(id);
    setIds((p) => { const n = new Set(p); has ? n.delete(id) : n.add(id); return n; });
    try {
      await api(has ? "delete" : "post", `/favorites/${id}`);
      toast.success(has ? "Removed from saved cars" : "Saved to your favourites");
    } catch (e) {
      setIds((p) => { const n = new Set(p); has ? n.add(id) : n.delete(id); return n; });
      toast.error(errMsg(e));
    } finally { pending.current.delete(id); }
  }, [ids, isSignedIn, api, navigate]);

  return <FavCtx.Provider value={{ ids, toggle }}>{children}</FavCtx.Provider>;
}

export const useFavorites = () => useContext(FavCtx);
